// utils/nativeBridge.js
// Bridge to the native upload service inside the Capacitor tablet shell (APK v2+).
// In that shell, captured photos are handed to Android WorkManager, which keeps
// uploading with the screen off, the app closed, and across reboots. In a plain
// browser or the old TWA the bridge is absent and everything falls back to the
// IndexedDB queue + service-worker sync.

function plugin() {
  try {
    return window.Capacitor?.Plugins?.PhotoUploader || null;
  } catch {
    return null;
  }
}

export function hasNativeUploader() {
  return !!plugin();
}

// The APK 2.0 worker (versionCode < 3) answers a single failed upload by
// retrying the WHOLE queue with exponential backoff, up to 5 hours, and new
// photos wait behind it. On those shells every photo also goes through the web
// queue (retried every minute while the app is open); the native queue stays as
// the safety net. Same photo id on both paths, so the server dedupes.
const RELIABLE_NATIVE_VERSION = 3;
let reliableNative = null;
export function nativeIsReliable() {
  if (!plugin()) return Promise.resolve(false);
  if (!reliableNative) {
    reliableNative = nativeGetAppInfo().then((info) => (info?.versionCode || 0) >= RELIABLE_NATIVE_VERSION);
  }
  return reliableNative;
}

// Hand the auth token to the native side so WorkManager can authenticate
// uploads long after the WebView is gone. Called on login and app start.
export async function nativeConfigure({ token }) {
  try {
    await plugin()?.configure({ token: token || '', baseUrl: window.location.origin });
  } catch {}
}

export async function nativeEnqueuePhoto({
  id, session_id, order_id, step_name, step_index,
  is_na, device_id, orientation, blob,
}) {
  const p = plugin();
  if (!p) throw new Error('native uploader unavailable');
  const dataB64 = blob ? await blobToBase64(blob) : null;
  await p.enqueue({
    id,
    sessionId: session_id,
    orderId: order_id,
    stepName: step_name,
    stepIndex: step_index,
    isNA: !!is_na,
    deviceId: device_id || '',
    orientation: orientation || 'landscape',
    dataB64,
  });
}

export async function nativeGetStats() {
  try {
    const s = await plugin()?.getStats();
    return s ? { pending: s.pending || 0, failed: s.failed || 0 } : null;
  } catch {
    return null;
  }
}

export async function nativeSyncNow() {
  try {
    await plugin()?.syncNow();
  } catch {}
}

// The shell reports its own version from PackageInfo, so the update banner
// stays correct for every future APK without web-side changes.
export async function nativeGetAppInfo() {
  try {
    const info = await plugin()?.getAppInfo();
    return info?.versionCode ? info : null;
  } catch {
    return null;
  }
}

// APK downloads can't be handled inside the Capacitor WebView; the native side
// opens /download in the system browser instead.
export async function nativeOpenDownloadPage() {
  try {
    await plugin()?.openDownloadPage();
    return true;
  } catch {
    return false;
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
